import { useRef, useState } from "react";
import { Button, Input, Radio, Select, Textarea } from "./components";

/**
 * Storno-Vorlagen (Migration 712, Antoine 12.10.2026: „Bei Stornobedingungen müssen abweichende
 * hinterlegt werden können, die dann ausgewählt werden können.").
 *
 * Gepflegt in den Einstellungen (`StornoVorlagenEditor`), ausgewählt am Angebot
 * (`StornoBedingungenFeld`). Das Angebot speichert weiterhin nur den TEXT — die Vorlage füllt das
 * Feld, danach ist es frei editierbar.
 */
export type StornoVorlage = { id: string; name: string; text: string; standard: boolean };

/** Gespeicherte Liste — oder, solange keine gepflegt ist, der bisherige Text als Vorlage „Standard". */
export function stornoVorlagenListe(d: { stornoVorlagen?: StornoVorlage[] | null; cancellationTerms?: string | null } | null | undefined): StornoVorlage[] {
  if (d?.stornoVorlagen?.length) return d.stornoVorlagen;
  const text = d?.cancellationTerms ?? "";
  return text.trim() ? [{ id: "standard", name: "Standard", text, standard: true }] : [];
}

const LABEL = "mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface";

export function StornoVorlagenEditor({
  vorlagen,
  onChange,
}: {
  vorlagen: StornoVorlage[];
  onChange: (neu: StornoVorlage[]) => void;
}) {
  const aendern = (id: string, patch: Partial<StornoVorlage>) =>
    onChange(vorlagen.map((v) => (v.id === id ? { ...v, ...patch } : patch.standard ? { ...v, standard: false } : v)));
  const loeschen = (id: string) => {
    const rest = vorlagen.filter((v) => v.id !== id);
    onChange(rest.some((v) => v.standard) ? rest : rest.map((v, i) => ({ ...v, standard: i === 0 })));
  };
  const hinzufuegen = () =>
    onChange([
      ...vorlagen,
      { id: crypto.randomUUID(), name: vorlagen.length ? `Vorlage ${vorlagen.length + 1}` : "Standard", text: "", standard: vorlagen.length === 0 },
    ]);

  return (
    <fieldset className="space-y-4">
      <legend className={LABEL}>Stornobedingungen (optional)</legend>
      <p className="font-body-sm text-body-sm text-on-surface-variant">
        Legen Sie abweichende Fassungen an und wählen Sie diese im Angebot aus. Neue Angebote starten mit dem Standard.
      </p>
      {vorlagen.map((v) => (
        <div key={v.id} className="space-y-2 rounded-lg border border-outline-variant/40 p-3 dark:border-outline/30">
          <div className="flex flex-wrap items-center gap-3">
            <Input
              aria-label="Name der Vorlage"
              className="max-w-xs"
              value={v.name}
              maxLength={80}
              onChange={(e) => aendern(v.id, { name: e.target.value })}
            />
            <label className="flex items-center gap-2 font-body-sm text-body-sm">
              <Radio name="storno-standard" checked={v.standard} onChange={() => aendern(v.id, { standard: true })} />
              Standard
            </label>
            <Button type="button" variant="danger" className="ml-auto" onClick={() => loeschen(v.id)}>
              Löschen
            </Button>
          </div>
          <Textarea
            aria-label={`Text der Vorlage ${v.name}`}
            className="resize-y"
            rows={5}
            maxLength={2000}
            value={v.text}
            onChange={(e) => aendern(v.id, { text: e.target.value })}
          />
        </div>
      ))}
      <Button type="button" variant="secondary" onClick={hinzufuegen}>
        Vorlage hinzufügen
      </Button>
    </fieldset>
  );
}

const EIGENER_TEXT = "__eigen__";

/**
 * Feld „Stornobedingungen" am Angebot: Auswahl „Vorlage" füllt das Textfeld, danach frei
 * editierbar. `onSpeichern` bekommt den Text (leer = Standard erben, wie bisher).
 */
export function StornoBedingungenFeld({
  vorlagen,
  startText,
  onSpeichern,
}: {
  vorlagen: StornoVorlage[];
  startText: string;
  onSpeichern: (text: string) => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const passend = (text: string) => vorlagen.find((v) => v.text.trim() === text.trim())?.id ?? EIGENER_TEXT;
  const [wahl, setWahl] = useState(() => passend(startText));

  return (
    <div className="space-y-2">
      {vorlagen.length > 1 && (
        <Select
          ariaLabel="Vorlage"
          value={wahl}
          onValueChange={(id) => {
            const v = vorlagen.find((x) => x.id === id);
            if (!v || !ref.current) return;
            ref.current.value = v.text;
            setWahl(id);
            onSpeichern(v.text);
          }}
          options={[
            ...vorlagen.map((v) => ({ value: v.id, label: v.standard ? `${v.name} (Standard)` : v.name })),
            ...(wahl === EIGENER_TEXT ? [{ value: EIGENER_TEXT, label: "Eigener Text" }] : []),
          ]}
        />
      )}
      <Textarea
        ref={ref}
        aria-label="Stornobedingungen"
        className="resize-y"
        rows={4}
        defaultValue={startText}
        placeholder="z. B. kostenfreie Stornierung bis 14 Tage vor der Veranstaltung"
        onChange={(e) => setWahl(passend(e.target.value))}
        onBlur={(e) => onSpeichern(e.target.value)}
      />
    </div>
  );
}
